export const REDACTED = "REDACTED";
/**
 * Span attributes that hold a URL or query string. The same rule as the
 * backend (`@rareconsult/observability`): values are redacted, keys stay visible.
 */
const URL_SPAN_ATTRIBUTES = new Set(["http.url", "url.full", "http.target"]);
const QUERY_SPAN_ATTRIBUTES = new Set(["url.query"]);
/**
 * Replaces every query parameter value with REDACTED, keeping the keys.
 * Query strings can carry credentials (access_token, OIDC code/state) and
 * user input (search terms), neither of which may end up in telemetry.
 */
export function redactQuery(query) {
    const redacted = new URLSearchParams();
    for (const key of new URLSearchParams(query).keys()) {
        redacted.set(key, REDACTED);
    }
    return redacted.toString();
}
/**
 * Redacts the query values of an absolute or relative URL and replaces any
 * fragment with REDACTED (fragments can carry tokens). The path stays, so
 * routes remain readable.
 */
export function redactUrl(url) {
    const fragmentStart = url.indexOf("#");
    const withoutFragment = fragmentStart === -1 ? url : url.slice(0, fragmentStart);
    const redactedFragment = fragmentStart === -1 ? "" : `#${REDACTED}`;
    const queryStart = withoutFragment.indexOf("?");
    if (queryStart === -1)
        return `${withoutFragment}${redactedFragment}`;
    const path = withoutFragment.slice(0, queryStart);
    const query = redactQuery(withoutFragment.slice(queryStart + 1));
    return `${path}?${query}${redactedFragment}`;
}
function looksLikeUrl(value) {
    return value.startsWith("http://") || value.startsWith("https://") || value.startsWith("/");
}
function redactUrlValues(record) {
    if (!record)
        return;
    for (const [key, value] of Object.entries(record)) {
        if (typeof value === "string" && looksLikeUrl(value))
            record[key] = redactUrl(value);
    }
}
function redactSpanAttributes(attributes) {
    for (const attribute of attributes ?? []) {
        const stringValue = attribute.value?.stringValue;
        if (!attribute.value || typeof stringValue !== "string")
            continue;
        if (URL_SPAN_ATTRIBUTES.has(attribute.key))
            attribute.value.stringValue = redactUrl(stringValue);
        if (QUERY_SPAN_ATTRIBUTES.has(attribute.key))
            attribute.value.stringValue = redactQuery(stringValue);
    }
}
function redactTraceSpans(traces) {
    for (const resourceSpans of traces.resourceSpans ?? []) {
        for (const scopeSpans of resourceSpans.scopeSpans ?? []) {
            for (const span of scopeSpans.spans ?? []) {
                redactSpanAttributes(span.attributes);
            }
        }
    }
}
function isRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}
/**
 * Faro `beforeSend` hook. Redacts query values and fragments in the page URL,
 * in browser span URL attributes (fetch/XHR), and in URL-valued event,
 * measurement and exception attributes (e.g. resource timing entries).
 * Log and error message text is not redacted.
 */
export function redactTransportItem(item) {
    if (item.meta.page?.url)
        item.meta.page.url = redactUrl(item.meta.page.url);
    const payload = item.payload;
    if (!isRecord(payload))
        return item;
    if (Array.isArray(payload["resourceSpans"]))
        redactTraceSpans(payload);
    if (isRecord(payload["attributes"]))
        redactUrlValues(payload["attributes"]);
    if (isRecord(payload["context"]))
        redactUrlValues(payload["context"]);
    return item;
}
//# sourceMappingURL=redaction.js.map